import React, { MouseEvent, useRef, useState } from 'react';

import Button from '@/components/V2/Button';
import { useDetectClickOutside } from '@/helpers/hooks';
import cn from '@/helpers/utils/classNames';
import { KeyboardArrowDownRounded, KeyboardArrowUpRounded } from '@mui/icons-material';
import { Box, Popper } from '@mui/material';

interface IngredientDropdownButtonProps<T extends Record<string, any>> {
  options: Array<T> | null;
  displayKey: keyof T;
  valueKey: keyof T;
  text: string;
  className?: string;
  btnClassName?: string;
  placeholder?: string;
  onSelect?: (value: T) => void;
  isFromPetRank?: boolean;
}

const IngredientDropdownButton = <T extends Record<string, any>>({
  options,
  valueKey,
  displayKey,
  text,
  className,
  btnClassName,
  placeholder,
  onSelect,
  isFromPetRank,
}: IngredientDropdownButtonProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<null | HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  useDetectClickOutside(() => {
    if (anchorEl) {
      setAnchorEl(null);
    }
  }, containerRef);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(open ? null : event.currentTarget);
    if (containerRef.current) {
      return setMenuWidth(containerRef.current.offsetWidth);
    }
  };

  const handleSelect = (option: T) => {
    onSelect && onSelect(option);
    setAnchorEl(null);
  };

  return (
    <div className={cn('v2-relative', className)} ref={containerRef}>
      <Button
        ref={buttonRef}
        type="button"
        variant="outlined"
        scheme="primary"
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        btnSize={'md'}
        className={btnClassName}
      >
        <div className="v2-flex v2-items-center v2-justify-between">
          {text ? <div className="v2-mr-1">{text}</div> : <div className="v2-mr-1 v2-max-w-[12rem]">{placeholder}</div>}
          {open ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
        </div>
      </Button>

      <Popper
        id="basic-button"
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        popperOptions={{
          placement: 'bottom',
        }}
        disablePortal={true}
        style={{ bottom: 0, position: 'absolute', zIndex: 3 }}
      >
        <Box
          sx={{
            border: '1px solid #EAECF0',
            boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
            backgroundColor: '#fff',
            width: menuWidth,
            marginTop: '6px',
            padding: '4px 0px',
            borderRadius: '4px',
            maxHeight: '338px',
            overflow: 'hidden',
            overflowY: 'auto',
          }}
        >
          {options?.map((option, i) => (
            <div
              className={cn('v2-px-4 v2-py-[9px] v2-text-wrap v2-cursor-pointer', {
                '!v2-bg-[#e2e8e6] !v2-mx-2 !v2-rounded-xs': text === option[displayKey] && isFromPetRank,
              })}
              key={i}
              onClick={() => handleSelect(option)}
            >
              <p className="v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-text-[#344054] v2-text-wrap v2-max-w-[308px]">
                {option[displayKey]}
              </p>
            </div>
          ))}
        </Box>
      </Popper>
    </div>
  );
};

export default IngredientDropdownButton;
